"use client"
import {
  Suspense,
  type CSSProperties,
  type ReactNode,
  memo,
  useCallback,
  useEffect,
  useInsertionEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import { GoodEndingHeader } from "@/app/playbook/plan-a/GoodEndingHeader";
import {
  MAIN_CONTENT_GRID_CLASSES,
  TEXT_COL_PX,
  TEXT_COL_WIDTH_CSS,
  HUD_COL_PX,
  CENTER_GUTTER_CSS,
  CENTER_GUTTER_MIN_PX,
  CENTER_GUTTER_MAX_PX,
  CENTER_GUTTER_VW_FRACTION,
  BAND_EDGE_PADDING_PX,
  SIDE_GUTTER_MIN_PX,
  BAND_MARGIN_LEFT_CSS,
  BAND_MARGIN_RIGHT_CSS,
  HUD_RIGHT_EDGE_CSS,
} from "@/app/playbook/plan-a/mainContentGridClasses";
import { type Globe2Variant } from "@/app/playbook/globe/variants";
import {
  PLAN_NODE_IDS,
  type ResolvedBranchesData,
  type UnifiedBranchesData,
} from "@/lib/branchTypes";
import { buildMarkdownLineCounts } from "./buildMarkdownLineCounts"
import { resolveBranchDataFromMarkdown } from "@/app/editor/resolveBranchDataFromMarkdown"
import { buildParentMap, buildPathToNode, buildPrimaryChain } from "./data/playbookStateUtils"
import { buildAnchorIndex } from "./data/anchorIndex"
import { useAnchorDeepLink } from "./hooks/useAnchorDeepLink"
import { type PlanAPhaseData, getDataForDate } from "@/data/plan-a/planATypes";
import { buildBranchPlaybookData, isBranchPlanId } from "@/data/plan-a/branchPhaseData";
import type { ExplainerModelSeries } from "@/data/plan-a/parsePlanAData";
import { useCountryWakeupById } from "@/data/plan-a/allCountryWakeupLevels";
import { useStateWakeup } from "@/data/plan-a/stateWakeupLevels";
// Lazy + mounted only while open: the globe settings editor reaches three.js
// (useContinents) and has no business in every visitor's First Load JS.
const GlobeControls2 = dynamic(
  () => import("@/app/playbook/globe/controls/GlobeControls2").then(m => m.GlobeControls2),
  { ssr: false }
);
import { PlaybookBranchNode } from "@/app/playbook/branch-node/PlaybookBranchNode";
import { PlanPrerenderWarmup } from "@/app/playbook/branch-node/PlanPrerenderWarmup";
import { PLAN_PRERENDER_WARMUP_EXCLUDE_SELECTOR } from "@/app/playbook/branch-node/planPrerenderWarmupScan";
import { PerspectiveFlatDocument } from "@/app/playbook/PerspectiveFlatDocument";
import { type MarkdownLineCounts } from "@/app/playbook/timeline/timelineUtils";
import { GlobeEditButtons } from "./GlobeEditButtons"
import { EditorLink } from "./EditorLink"
import { MobileHUD } from "./plan-a/MobileHUD"
import { DoNotShareModal } from "./plan-a/DoNotShareModal"
import { IS_PREVIEW_MODE } from "@/lib/previewMode"
import { usePlaybookState } from "./hooks/usePlaybookState"
import { useBranchHoverAutoplayDate } from "./hooks/useBranchHoverAutoplayDate"
import { CHOOSE_PATH_DIAGRAM_DESIGNS, HUD_DETAILS_LADDER_HIGHLIGHTS, HUD_DETAILS_LADDER_HIGHLIGHT_DEFAULT, HUD_METRIC_PANEL_DESIGNS, HUD_VARIANTS, MOBILE_HUD_DESIGNS } from "./hud/variants"
import { DetailsLadderHighlightContext } from "./hud/metric-details/panel-redesigns/metricPanelRedesigns"
import { ChoosePathDesignContext } from "./branch-node/choosePathDesigns"
import type { MetricPanelDesignId } from "./hud/metric-details/panel-redesigns/metricPanelRedesigns"
import { HudVariantLayout } from "./hud/HudVariantLayout"
import { MetricDetailsProvider, useMetricDetails } from "./hud/metric-details/metricDetailsContext"
import { MetricDetailsHudState } from "./hud/metric-details/MetricDetailsHudState"
import { DetailsYearPill } from "./hud/metric-details/DetailsYearPill"
import { useDetailsClickAway } from "./hud/metric-details/useDetailsClickAway"
import type { MetricId } from "./hud/metric-details/metricDetailsTypes"
import {
  HudContextProvider,
  HudSlideUpStyles,
  hudSlideUpClass,
  useScrollSlideFallback,
  HUD_SLIDE_UP_FROM_PX,
  type PlaybookHUDProps,
  useHudContext,
} from "./hud/shared/playbookHUDInfra"
import { BackgroundPatternControls } from "./extrapolated-graph/BackgroundPatternControls";
import {
  BackgroundPatternSettingsProvider,
} from "./extrapolated-graph/BackgroundPatternSettingsProvider";
import {
  LossOfControlEditorProvider,
} from "./extrapolated-graph/LossOfControlEditorProvider";
import { type BackgroundPatternSettings } from "./extrapolated-graph/backgroundPatternStore";
import {
  PerspectiveTabContext,
} from "@/app/common/blocks/perspectiveTabContext"
import type {
  PerspectiveCategory,
} from "@/app/common/blocks/perspectiveTabContext"
import {
  ChoicePointScrollLock,
} from "./branch-node/playbook-branch-choices/ChoicePointScrollLock"
const GalaxyVisualizationEmbed = dynamic(
  () => import("@/app/playbook/galaxies/GalaxyVisualizationEmbed"),
  { ssr: false }
)

type Props = {
  branchData: ResolvedBranchesData
  globeVariants: Globe2Variant[]
  playbookData: PlanAPhaseData[]
  explainerModelSeries?: ExplainerModelSeries
  markdownLineCounts?: MarkdownLineCounts
  initialBackgroundPatternSettings: BackgroundPatternSettings
  sourceMarkdownFilename?: string
  defaultSourceMarkdownFilename?: string
  storyMarkdownVersions?: { filename: string; createdMs: number }[]
  favoritedStoryVersionFilenames?: string[]
  // Lets EditorLink rebuild branchData in-browser from cached markdown.
  existingUnifiedJson: UnifiedBranchesData
  // On /perspective/[category] pages: which flat doc the page renders.
  // Passed from the server (never read from useSearchParams here — that
  // would make the static routes prerender their Suspense fallback).
  pagePerspective?: "public" | "insider"
  // When set (on /perspective/[category]), the center column renders this
  // flat doc instead of the branching playbook.
  perspectiveDocMarkdown?: string
  // Per-POV bodies (intro stripped) for inline expansion under the
  // end-of-Plan-A diagram. Undefined per-key if that file is absent.
  perspectiveBodies?: { public?: string; insider?: string }
}

const PLAYBOOK_ACCENT_COLOR = "var(--race-background)"
// The last dateMarker BEFORE the plan-choice flowchart (end of the 2027
// "writing on the wall" section). Until the reader actually picks a plan,
// the mobile overlay's date is clamped here: the pre-choice scroll window
// reaches into the Plan A preview, whose 2029-07 marker would otherwise
// advance the strip (and fire its frame flop) with no plan chosen.
const PRE_CHOICE_MOBILE_HUD_MAX_DATE = new Date("2029-01-01T00:00:00Z")
// How long after picking a plan the reader's touch/wheel/key input is still
// treated as part of the selection gesture rather than as "reading on" (the
// signal that releases the post-choice date clamp below).
const POST_CHOICE_RELEASE_MIN_DELAY_MS = 600
const DESKTOP_HUD_MEDIA_QUERY = "(min-width: 768px)"

// A single dim shade backs the whole epilogue: it fades in as soon as the
// epilogue opens (isEpilogueScrolledTo — the Epilogue button engaging) and the
// galaxy layer that fades in later (showGalaxy = galaxyProgress > 0,
// useGalaxy.ts) reuses the same color, so the backdrop never darkens further.
const EPILOGUE_INTERMEDIATE_BACKGROUND_COLOR = "#D0CDBF"

// On mobile the epilogue keeps the plain page background and renders no galaxy
// scene: both the dim/galaxy backdrop color and the WebGL galaxy layer are
// suppressed. Flip this to true to bring the mobile epilogue galaxies back.
const EPILOGUE_GALAXIES_ON_MOBILE = false

// Dither the epilogue galaxy scene through a dot mask. Toggled off for now —
// flip to true to bring the halftone look back.
const GALAXY_DITHER_ENABLED = false
const GALAXY_DITHER_DOT_MASK =
  "radial-gradient(circle, #000 0 1.45px, transparent 1.6px)"
const GALAXY_DITHER_DOT_MASK_STYLE: CSSProperties | undefined = GALAXY_DITHER_ENABLED
  ? {
      WebkitMaskImage: GALAXY_DITHER_DOT_MASK,
      maskImage: GALAXY_DITHER_DOT_MASK,
      WebkitMaskSize: "5px 5px",
      maskSize: "5px 5px",
      WebkitMaskPosition: "0 0",
      maskPosition: "0 0",
    }
  : undefined

const useIsDesktopHudViewport = () => {
  const [isDesktopViewport, setIsDesktopViewport] = useState<boolean | null>(null)

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_HUD_MEDIA_QUERY)
    const updateIsDesktopViewport = () => {
      setIsDesktopViewport(mediaQuery.matches)
    }
    updateIsDesktopViewport()
    mediaQuery.addEventListener("change", updateIsDesktopViewport)
    return () => mediaQuery.removeEventListener("change", updateIsDesktopViewport)
  }, [])

  return isDesktopViewport
}

// HudShell owns the slide-up animation, hover plumbing, and the
// context provider feeding every panel. Variants are () => ReactNode.
// `metricPanelDesign` picks the details-panel treatment for the active HUD
// tab (HUD_METRIC_PANEL_DESIGNS); null = production content.
const HudShell = memo(({
  render,
  metricPanelDesign = null,
  ...props
}: PlaybookHUDProps & { render: () => ReactNode; metricPanelDesign?: MetricPanelDesignId | null }) => {
  return (
    <HudContextProvider {...props}>
      <HudShellContainer render={render} metricPanelDesign={metricPanelDesign} />
    </HudContextProvider>
  )
})
HudShell.displayName = "HudShell"

const HudShellContainer = ({
  render,
  metricPanelDesign = null,
}: {
  render: () => ReactNode
  metricPanelDesign?: MetricPanelDesignId | null
}) => {
  const ctx = useHudContext()
  const slideFallbackRef = useScrollSlideFallback(HUD_SLIDE_UP_FROM_PX)
  // Clicking anywhere outside the HUD column closes an open details state.
  useDetailsClickAway(ctx.epilogue.hudContainerRef)
  // An open metric shows as an alternative HUD state in an overlay ABOVE the
  // variant body — both layers stay mounted at all times, so the whole
  // open/close choreography is CSS transitions on stable elements (no body
  // swap, no phase timers, nothing to jump at a mount seam). The choreography
  // is the SEQUENTIAL FADE: the leaving layer fades fully out (then hides via
  // delayed `visibility`), and only then does the arriving layer fade in —
  // see sequentialFadeLayerStyles in detailAnimations.tsx for the timing.
  const { openId } = useMetricDetails()
  // The metric the details layer shows. Outlives openId during the close fade
  // (so content dissolves rather than vanishing); `gen` bumps on every open so
  // the content's entrance animations replay even for the same metric.
  const [displayed, setDisplayed] = useState<{ id: MetricId; gen: number } | null>(
    openId ? { id: openId, gen: 0 } : null
  )
  // Previous-value-in-STATE (not a ref): a discarded concurrent render would
  // keep a ref mutation but lose the setDisplayed, desyncing the two.
  const [prevOpenId, setPrevOpenId] = useState(openId)
  if (openId !== prevOpenId) {
    setPrevOpenId(openId)
    if (openId) setDisplayed((d) => ({ id: openId, gen: (d?.gen ?? 0) + 1 }))
  }

  return (
    <div
      // Merge the epilogue container ref with the slide fallback ref.
      ref={(node) => {
        ctx.epilogue.hudContainerRef.current = node
        slideFallbackRef.current = node
      }}
      className={`relative flex flex-col w-full h-full ${hudSlideUpClass("hud")}`}
    >
      <HudSlideUpStyles slideUpId="hud" fromTranslateYPx={HUD_SLIDE_UP_FROM_PX} />
      <div
        className="flex-1 min-h-0 relative"
        onMouseEnter={() => ctx.setIsHudHovered(true)}
        onMouseLeave={() => ctx.setIsHudHovered(false)}
      >
        {/* The open/close choreography is the SEQUENTIAL FADE: the leaving
            layer fades fully out over 140ms, THEN the arriving layer fades in
            over 200ms (the 140ms transition-delay) — no crossfade, and
            nothing travels (the FLIP flights — FlyingMetricLabel /
            FlyingPanelElements — are not mounted), with ONE exception: the
            year pill persists across the state change. DetailsYearPill (in
            the persistent overlay below, exempt from both layer fades) IS
            the pill while details are open — it hides the graph's copy,
            glides to the panel header, and glides home on close. The arriving side also
            holds `visibility: hidden` through the delay (the delayed
            visibility transition), so a layer at opacity 0 is never
            hit-testable — a second click during the handoff can't land on an
            invisible control. Asymmetric easing per hoverSwapFade.tsx's
            convention: arriving eases out, leaving eases in. All of it is
            classes (not inline styles) so motion-reduce:transition-none
            can neutralize every transition for reduced-motion users.
            The [&_a:hover]/[&_button:hover] variants on the leaving side
            resolve var(--accent) to black under :hover — a red-hovered back
            chip or header link snaps to its idle color the instant the close
            starts instead of staying red as it fades away (content reds use
            var(--race-background), so nothing else changes color). */}
        {/* Variant layer — the standard HUD. */}
        <div
          className={`h-full motion-reduce:transition-none ${
            openId
              ? "pointer-events-none invisible opacity-0 [transition:opacity_140ms_ease-in,visibility_0s_140ms] [&_a:hover]:[--accent:#111111] [&_button:hover]:[--accent:#111111] [&_[role=button]:hover]:[--accent:#111111]"
              : "visible opacity-100 [transition:opacity_200ms_ease-out_140ms,visibility_0s_140ms]"
          }`}
        >
          <HudVariantLayout render={render} />
        </div>
        {/* Details layer — the open metric as an alternative HUD state. Its
            sections animate themselves in (rise + fade onto the already-empty
            ground); the layer's own fade waits out the variant layer's exit. */}
        <div
          className={`absolute inset-0 z-[15] motion-reduce:transition-none ${
            openId
              ? "visible opacity-100 [transition:opacity_200ms_ease-out_140ms,visibility_0s_140ms]"
              : "pointer-events-none invisible opacity-0 [transition:opacity_140ms_ease-in,visibility_0s_140ms] [&_a:hover]:[--accent:#111111] [&_button:hover]:[--accent:#111111] [&_[role=button]:hover]:[--accent:#111111]"
          }`}
        >
          {displayed && (
            <MetricDetailsHudState key={`${displayed.id}-${displayed.gen}`} metricId={displayed.id} panelDesign={metricPanelDesign} />
          )}
        </div>
        <DetailsYearPill />
      </div>
    </div>
  )
}

export const Playbook = ({
  branchData: initialBranchData,
  globeVariants,
  playbookData,
  explainerModelSeries,
  markdownLineCounts: initialMarkdownLineCounts,
  initialBackgroundPatternSettings,
  sourceMarkdownFilename: initialSourceMarkdownFilename,
  defaultSourceMarkdownFilename,
  storyMarkdownVersions,
  favoritedStoryVersionFilenames,
  existingUnifiedJson,
  perspectiveDocMarkdown,
  perspectiveBodies,
  pagePerspective,
}: Props) => {
  // Branch data lives in state so EditorLink can swap versions
  // client-side from prefetched markdown (URL still updated for
  // shareability, but the data shown comes from this state).
  const [branchData, setBranchData] = useState(initialBranchData)
  const [markdownLineCounts, setMarkdownLineCounts] = useState(initialMarkdownLineCounts)
  const [sourceMarkdownFilename, setSourceMarkdownFilename] = useState(initialSourceMarkdownFilename)
  // Re-sync when the server flows new props down (direct URL load,
  // server-path navigation).
  useEffect(() => { setBranchData(initialBranchData) }, [initialBranchData])
  useEffect(() => { setMarkdownLineCounts(initialMarkdownLineCounts) }, [initialMarkdownLineCounts])
  useEffect(() => { setSourceMarkdownFilename(initialSourceMarkdownFilename) }, [initialSourceMarkdownFilename])

  const swapToVersionMarkdown = useCallback((filename: string, fullMarkdown: string): boolean => {
    const next = resolveBranchDataFromMarkdown(fullMarkdown, existingUnifiedJson)
    if (!next) return false
    setBranchData(next)
    setMarkdownLineCounts(buildMarkdownLineCounts(next))
    setSourceMarkdownFilename(filename)
    return true
  }, [existingUnifiedJson])

  const [globeVariantsState, setGlobeVariantsState] = useState(globeVariants)
  const [activeVariantIndex, setActiveVariantIndex] = useState(0)
  const [isEditOpen, setIsEditOpen] = useState(false)
  // Three mutually-exclusive end-of-Plan-A endings (Epilogue + Public/
  // Insider POV); the setters below enforce only-one-open. isEpilogue-
  // Expanded stays separate because HUD/galaxy/scroll read it directly.
  const [isEpilogueExpanded, setIsEpilogueExpanded] = useState(false)
  const [activePov, setActivePov] = useState<Exclude<PerspectiveCategory, "mainScenario"> | null>(null)
  // Expand the epilogue ending — closing any open POV (shared slot).
  const expandEpilogue = useCallback(() => {
    setActivePov(null)
    setIsEpilogueExpanded(true)
  }, [])
  // Expand a POV ending in place — closing the epilogue (one slot).
  const expandPov = useCallback((perspective: Exclude<PerspectiveCategory, "mainScenario">) => {
    setIsEpilogueExpanded(false)
    setActivePov(perspective)
  }, [])
  // Close any open ending when leaving Plan A: its endings belong to
  // the Plan A end-cap, so another plan should start collapsed.
  const collapseEndings = useCallback(() => {
    setIsEpilogueExpanded(false)
    setActivePov(null)
  }, [])
  const [isAgentPopulationHidden, setIsAgentPopulationHidden] = useState(false)
  const [isPolicyGraphHidden, setIsPolicyGraphHidden] = useState(false)
  const [modalDismissed, setModalDismissed] = useState(false)
  // "Read another branch" promotes the perspective selector above the
  // root choice point's flowchart so both options show together.
  const [showPerspectiveAboveFlowchart, setShowPerspectiveAboveFlowchart] = useState(false)
  const router = useRouter()
  // Page-level mode: public/insider render their own server doc on
  // /perspective/[category], mainScenario the branching playbook on / —
  // so changing it navigates rather than flipping client state.
  const selectedPerspective: PerspectiveCategory = pagePerspective ?? "mainScenario"

  const setSelectedPerspective = useCallback(
    (value: PerspectiveCategory | null) => {
      const next = value ?? "mainScenario"
      // No hash carried over so the new document lands at its own top.
      router.push(next === "mainScenario" ? "/" : `/perspective/${next}`)
    },
    [router]
  )

  const {
    activePlan,
    contentSectionRef,
    metricComparisonPlan,
    galaxyFadeInDurationMs,
    galaxyProgress,
    graphDate,
    hoveredBranchTargetId,
    hoveredPlan,
    hudActivePlan,
    hudSelectedPlan,
    isChoicePointOnScreen,
    isEpilogueScrolledTo,
    legendMarkerPositions,
    selectNode,
    selectedPath,
    selectedPlan,
    setHoveredBranchTargetId,
    setHoveredPlan,
    setSelectedPath,
    showGalaxy,
  } = usePlaybookState(branchData, isEpilogueExpanded)
  const hoverAutoplayDate = useBranchHoverAutoplayDate({
    branchData,
    hoveredBranchTargetId,
    hoveredPlan,
    baseDate: graphDate,
  })
  const hudDate = hoverAutoplayDate ?? graphDate
  // The mobile overlay freezes at the choice point until a plan is picked:
  // scrolling the pre-choice window (down into the Plan A preview and back)
  // must not change it. Plan hover/tap autoplay teasers still sweep — that
  // date is deliberate plan preview, not scroll.
  //
  // The pick itself must not move it either (Joanna's 2026-07-09 keyframe
  // pass: "don't change when you select" / "freeze when I click — do
  // NOTHING"). Two traps make naive releases leap the strip at the click:
  // (1) selectNode always scrolls and graphDate recomputes on programmatic
  // scrolls, so "release on first date change" fires at the click itself;
  // (2) graphDate is the last marker above the 60%-of-viewport line, and
  // the selection's scroll/reflow can land plan A's 2029-07 marker already
  // ABOVE that line — so even an input-armed release would REVEAL a date
  // that is already past the flop beat and leap anyway.
  // So the release is two-staged: the reader's first deliberate input
  // (touchmove / wheel / keydown — programmatic scrolls can't produce
  // these; a sloppy selection tap is debounced away) only records the raw
  // date as a BASELINE while the strip stays frozen at the choice date.
  // The story goes live — permanently — only when a marker fires BEYOND
  // that baseline: something the reader actually read their way past.
  const graphDateRef = useRef(graphDate)
  graphDateRef.current = graphDate
  const [postChoiceBaselineT, setPostChoiceBaselineT] = useState<number | null>(null)
  const [isPostChoiceDateLive, setIsPostChoiceDateLive] = useState(false)
  useEffect(() => {
    if (selectedPlan === null) {
      setPostChoiceBaselineT(null)
      setIsPostChoiceDateLive(false)
    }
  }, [selectedPlan])
  useEffect(() => {
    if (selectedPlan === null || postChoiceBaselineT !== null) return
    const armedAt = performance.now()
    const recordBaseline = () => {
      // Finger drift within the selection tap itself (touchmove fires for
      // a few px of slip) must not count as reading on.
      if (performance.now() - armedAt < POST_CHOICE_RELEASE_MIN_DELAY_MS) return
      setPostChoiceBaselineT(Math.max(
        graphDateRef.current.getTime(),
        PRE_CHOICE_MOBILE_HUD_MAX_DATE.getTime(),
      ))
    }
    window.addEventListener("touchmove", recordBaseline, { passive: true })
    window.addEventListener("wheel", recordBaseline, { passive: true })
    window.addEventListener("keydown", recordBaseline)
    return () => {
      window.removeEventListener("touchmove", recordBaseline)
      window.removeEventListener("wheel", recordBaseline)
      window.removeEventListener("keydown", recordBaseline)
    }
  }, [selectedPlan, postChoiceBaselineT])
  useEffect(() => {
    if (
      !isPostChoiceDateLive
      && postChoiceBaselineT !== null
      && graphDate.getTime() > postChoiceBaselineT
    ) {
      setIsPostChoiceDateLive(true)
    }
  }, [graphDate, postChoiceBaselineT, isPostChoiceDateLive])
  const mobileHudDate = hoverAutoplayDate
    ?? (isPostChoiceDateLive
      ? graphDate
      : graphDate.getTime() > PRE_CHOICE_MOBILE_HUD_MAX_DATE.getTime()
        ? PRE_CHOICE_MOBILE_HUD_MAX_DATE
        : graphDate)
  if (process.env.NODE_ENV === "development" && typeof window !== "undefined") {
    // Debug tap for the date-pipeline smoothness hunt (dev-only).
    ;(window as unknown as Record<string, unknown>).__playbookDates = {
      graphDate: graphDate.getTime(),
      hoverAutoplayDate: hoverAutoplayDate?.getTime() ?? null,
      hoveredPlan,
      hoveredBranchTargetId,
    }
  }
  // While branch-hover autoplay drives the date, the globe holds its last
  // pre-epilogue framing (skipping the 2041+ zoom-away keyframes) so the
  // teaser stays on the forecast and out of the epilogue.
  const isHoverPreviewActive = hoverAutoplayDate !== null
  // The HUD follows the active plan: branch plans (B/C/D/S) get their
  // generated phase data (shared Plan A history up to the 2029 branch
  // point, then per-plan hudMetrics); Plan A keeps the full CSV data.
  const hudPlaybookData = useMemo(
    () =>
      isBranchPlanId(hudActivePlan)
        ? buildBranchPlaybookData(playbookData, hudActivePlan)
        : playbookData,
    [playbookData, hudActivePlan]
  )

  const perspectiveTabContextValue = useMemo(
    () => ({
      selectedPerspective,
      setSelectedPerspective,
    }),
    [selectedPerspective, setSelectedPerspective]
  )
  const US_COUNTRY_ID = 840
  const defaultCountryWakeup = useCountryWakeupById(hudDate)
  const countryWakeup = useMemo(() => {
    const currentPhase = getDataForDate(hudPlaybookData, hudDate)
    if (!currentPhase) return defaultCountryWakeup
    const usPlanAWakeup = currentPhase.countryWakeup.find(
      (e) => e.country === "US"
    )
    if (!usPlanAWakeup) return defaultCountryWakeup
    let hasUS = false
    const merged = defaultCountryWakeup.map((e) => {
      if (e.countryId !== US_COUNTRY_ID) return e
      hasUS = true
      return { ...e, level: usPlanAWakeup.level }
    })
    if (!hasUS) {
      merged.push({ countryId: US_COUNTRY_ID, level: usPlanAWakeup.level })
    }
    return merged
  }, [defaultCountryWakeup, hudPlaybookData, hudDate])
  const stateWakeup = useStateWakeup(hudDate)
  const activeGlobeVariant = globeVariantsState[activeVariantIndex] || globeVariantsState[0]
  const pageTextColor = activeGlobeVariant?.settings.backgroundTheme?.textColor ?? "#000"
  const pageBackgroundColor = activeGlobeVariant?.settings.backgroundTheme?.backgroundColor ?? "#fffff8"
  const [galaxyTranslateX, setGalaxyTranslateX] = useState(0)
  // Viewport x of the text column's right edge; galaxies projecting left of it
  // are non-hoverable so their tooltip never appears behind the reading column.
  const [galaxyHoverExclusionLeft, setGalaxyHoverExclusionLeft] = useState(0)
  // The galaxy embed runs a full-screen WebGL loop from mount (even at
  // opacity 0), so stay unmounted until the epilogue engages. Mounting at
  // the Epilogue click (isEpilogueScrolledTo) rather than first visibility
  // (showGalaxy) gives the scene's Earth texture time to fetch+rasterize
  // while the reader is still above the header, so the globe→scene handoff
  // never shows the untextured placeholder sphere.
  const isGalaxyEngaged = (isEpilogueExpanded && isEpilogueScrolledTo) || showGalaxy
  const [isGalaxyMounted, setIsGalaxyMounted] = useState(false)
  useEffect(() => {
    if (isGalaxyEngaged) {
      setIsGalaxyMounted(true)
      return
    }
    const unmountTimeoutId = window.setTimeout(
      () => setIsGalaxyMounted(false),
      galaxyFadeInDurationMs
    )
    return () => window.clearTimeout(unmountTimeoutId)
  }, [isGalaxyEngaged, galaxyFadeInDurationMs])
  // The intermediate epilogue dim stays mounted through its fade-out, then
  // unmounts entirely. A permanently-mounted fixed inset-0 overlay — even at
  // opacity 0 — is composited, and every stacking context above it "overlaps
  // composited content": the whole 40,000px text column was being promoted
  // to its own GPU layer and repainted on scroll (hundreds of MB of
  // IOSurface memory on a long read).
  const isEpilogueDimEngaged = isEpilogueExpanded && isEpilogueScrolledTo
  const [isEpilogueDimMounted, setIsEpilogueDimMounted] = useState(false)
  useEffect(() => {
    if (isEpilogueDimEngaged) {
      setIsEpilogueDimMounted(true)
      return
    }
    const unmountTimeoutId = window.setTimeout(
      () => setIsEpilogueDimMounted(false),
      galaxyFadeInDurationMs
    )
    return () => window.clearTimeout(unmountTimeoutId)
  }, [isEpilogueDimEngaged, galaxyFadeInDurationMs])
  const isDesktopHudViewport = useIsDesktopHudViewport()
  // MobileHUD is on by default again (it was temporarily opt-in via
  // ?mobilehud while 9ed6cd0f chased the mobile Safari crash). ?nomobilehud
  // is the escape hatch to pull it back off for on-device A/B testing.
  // Read once at mount — a load-time switch, same pattern as ?dither2d.
  const [isMobileHudEnabled] = useState(
    () =>
      typeof window === "undefined"
      || !window.location.search.includes("nomobilehud")
  )
  // Mobile is only known once useIsDesktopHudViewport resolves (=== false);
  // treat the null initial state as "not mobile" so desktop/SSR never flashes
  // the suppressed epilogue. When off on mobile, the epilogue keeps the page
  // background and the galaxy scene stays unmounted.
  const epilogueGalaxiesEnabled =
    EPILOGUE_GALAXIES_ON_MOBILE || isDesktopHudViewport !== false
  const [hudVersionIndex, setHudVersionIndex] = useState(0)
  // ?hud=N (debug: preview a HUD variant) is read post-mount from the raw URL
  // rather than useSearchParams — a useSearchParams read here would make the
  // static routes prerender their Suspense fallback instead of the content.
  useEffect(() => {
    const raw = new URLSearchParams(window.location.search).get("hud")
    if (raw === null) return
    const parsed = parseInt(raw, 10)
    if (Number.isNaN(parsed) || parsed < 0 || parsed >= HUD_VARIANTS.length) return
    setHudVersionIndex(parsed)
  }, [])
  const activeHudVariant = HUD_VARIANTS[hudVersionIndex] ?? HUD_VARIANTS[0]
  const activeMetricPanelDesign = HUD_METRIC_PANEL_DESIGNS[hudVersionIndex] ?? null
  // Details-page ladder treatment for THIS tab (matches the tab's takeover
  // ladder) — see HUD_DETAILS_LADDER_HIGHLIGHTS in hud/variants.tsx.
  const activeDetailsLadderHighlight =
    HUD_DETAILS_LADDER_HIGHLIGHTS[hudVersionIndex] ?? HUD_DETAILS_LADDER_HIGHLIGHT_DEFAULT
  const currentBranchNodeTitle = useMemo(() => {
    const currentNodeId = selectedPath[selectedPath.length - 1]
    return currentNodeId ? branchData.nodes[currentNodeId]?.title : undefined
  }, [branchData.nodes, selectedPath])
  const handleHudNodeSelect = useCallback(
    (nodeId: string, scrollTargetId?: string) => {
      selectNode(nodeId, { scrollTargetId })
    },
    [selectNode]
  )

  useEffect(() => {
    // Dev-only HUD-variant cycling. Never ship it: the handler preventDefaults
    // meta+arrow, which is back/forward navigation on macOS.
    if (process.env.NODE_ENV === "production") return
    const handleHudSwitch = (e: KeyboardEvent) => {
      if (!e.metaKey) return
      if (e.key === "ArrowLeft") {
        e.preventDefault()
        setHudVersionIndex(i =>
          (i - 1 + HUD_VARIANTS.length) % HUD_VARIANTS.length
        )
      } else if (e.key === "ArrowRight") {
        e.preventDefault()
        setHudVersionIndex(i => (i + 1) % HUD_VARIANTS.length)
      }
    }
    window.addEventListener("keydown", handleHudSwitch)
    return () => window.removeEventListener("keydown", handleHudSwitch)
  }, [])

  useEffect(() => {
    const url = new URL(window.location.href)
    if (hudVersionIndex === 0) {
      url.searchParams.delete("hud")
    } else {
      url.searchParams.set("hud", String(hudVersionIndex))
    }
    window.history.replaceState({}, "", url.toString())
  }, [hudVersionIndex])

  const parentMap = useMemo(() => buildParentMap(branchData), [branchData])
  const anchorIndex = useMemo(() => buildAnchorIndex(branchData), [branchData])
  const hasUserNavigatedRef = useRef(false)
  const setSelectedPathFromUser = useCallback(
    (path: string[], options?: Parameters<typeof setSelectedPath>[1]) => {
      hasUserNavigatedRef.current = true
      setSelectedPath(path, options)
    },
    [setSelectedPath],
  )
  useAnchorDeepLink({
    branchData,
    anchorIndex,
    parentMap,
    setSelectedPath,
    hasUserNavigatedRef,
    onExpandEpilogue: expandEpilogue,
    onExpandPov: expandPov,
    onCollapseEndings: collapseEndings,
  })
  // Collapse open endings when SWITCHING plans (shared state would
  // otherwise leak Plan A's open epilogue into another plan's inline
  // copy). Ref-guarded to fire only on real plan-to-plan changes, not
  // the initial null→plan resolve (which would undo a #playbook-
  // epilogue deep link).
  const prevSelectedPlanRef = useRef(selectedPlan)
  useEffect(() => {
    const prev = prevSelectedPlanRef.current
    prevSelectedPlanRef.current = selectedPlan
    if (prev !== null && selectedPlan !== null && prev !== selectedPlan) {
      collapseEndings()
    }
  }, [selectedPlan, collapseEndings])

  // Cmd-F: expand the current plan's primary chain and every collapsed
  // <details> so the browser's find-in-page can search all the text.
  useEffect(() => {
    const handleExpandAll = (e: KeyboardEvent) => {
      if (!e.metaKey || e.key !== "f") return
      if (selectedPlan !== null) {
        const planNodeId = PLAN_NODE_IDS[selectedPlan]
        const pathToPlan = buildPathToNode(planNodeId, parentMap)
        if (pathToPlan.length > 0) {
          const primaryChain = buildPrimaryChain(planNodeId, branchData.nodes)
          setSelectedPath([...pathToPlan.slice(0, -1), ...primaryChain], { scrollTargetNodeId: null })
        }
      }
      requestAnimationFrame(() => {
        const detailsElements = document.querySelectorAll<HTMLDetailsElement>(
          `details${PLAN_PRERENDER_WARMUP_EXCLUDE_SELECTOR}`
        )
        for (const detailsElement of detailsElements) {
          detailsElement.open = true
        }
      })
    }
    window.addEventListener("keydown", handleExpandAll)
    return () => window.removeEventListener("keydown", handleExpandAll)
  }, [branchData, parentMap, selectedPlan, setSelectedPath])

  useEffect(() => {
    // Center the galaxy embed on the HUD column. Mirror the CSS
    // geometry in mainContentGridClasses so the two never drift:
    // centerGutter = clamp(min, min(VW_FRACTION*vw, availableAfterText),
    // max) — the available term collapses the gutter to protect the text
    // column; bandWidth = 2*padding + text + centerGutter + HUD;
    // sideGutter = max(minGutter, (vw-bandWidth)/2); the HUD's right edge
    // sits sideGutter+padding from the viewport, so its center is half a
    // HUD-width inside that.
    const computeOffset = () => {
      const vw = window.innerWidth
      const centerGutterAvailable =
        vw -
        (2 * BAND_EDGE_PADDING_PX + TEXT_COL_PX + HUD_COL_PX + 2 * SIDE_GUTTER_MIN_PX)
      const centerGutter = Math.min(
        CENTER_GUTTER_MAX_PX,
        Math.max(
          CENTER_GUTTER_MIN_PX,
          Math.min(vw * CENTER_GUTTER_VW_FRACTION, centerGutterAvailable)
        )
      )
      const bandWidth =
        2 * BAND_EDGE_PADDING_PX + TEXT_COL_PX + centerGutter + HUD_COL_PX
      const sideGutter = Math.max(SIDE_GUTTER_MIN_PX, (vw - bandWidth) / 2)
      const hudRightEdgeFromViewport = sideGutter + BAND_EDGE_PADDING_PX
      const hudCenter = vw - hudRightEdgeFromViewport - HUD_COL_PX / 2
      setGalaxyTranslateX(hudCenter - vw / 2)
      // The text column's right screen edge — galaxies whose center projects
      // left of this sit behind the reading column, so their tooltip is
      // suppressed (see GalaxyVisualizationEmbed's hoverExclusionLeftPx). Mirror
      // TEXT_COL_WIDTH_CSS: full 700px until the viewport is too narrow to hold
      // the cluster, then the room left after gutters + HUD + min center gutter.
      const textColWidth = Math.min(
        TEXT_COL_PX,
        vw - 2 * sideGutter - (2 * BAND_EDGE_PADDING_PX + HUD_COL_PX + CENTER_GUTTER_MIN_PX)
      )
      setGalaxyHoverExclusionLeft(sideGutter + BAND_EDGE_PADDING_PX + textColWidth)
    }
    computeOffset()
    window.addEventListener("resize", computeOffset)
    return () => window.removeEventListener("resize", computeOffset)
  }, [])

  const handleAdvanceVariantNow = useCallback(() => {
    setActiveVariantIndex((prev) => {
      if (globeVariantsState.length === 0) return 0
      return (prev + 1) % globeVariantsState.length
    })
  }, [globeVariantsState.length])

  // useInsertionEffect, NOT useEffect: GroupedSquaresCanvas (the labor dot
  // strip) resolves var(--accent) via getComputedStyle inside mount LAYOUT
  // effects and caches the result forever. A plain effect here runs after
  // those, so the canvas would capture globals.css's green default instead
  // of the playbook red. Insertion effects fire before any layout effect,
  // so the accent is on the root before anything resolves it.
  useInsertionEffect(() => {
    const root = document.documentElement
    root.style.setProperty("--accent", PLAYBOOK_ACCENT_COLOR)
    return () => { root.style.removeProperty("--accent") }
  }, [])

  // Live backdrop in step with the overlays (cream → dim → galaxy),
  // published as --playbook-epilogue-bg so elements that paint their
  // own page background (diagram labels/dots, reviewer box) match and
  // fade with it instead of a hardcoded #fffff8.
  const effectiveEpilogueBackgroundColor =
    epilogueGalaxiesEnabled && isEpilogueExpanded && isEpilogueScrolledTo
      ? EPILOGUE_INTERMEDIATE_BACKGROUND_COLOR
      : pageBackgroundColor

  // The globe's sphere disc paints its own background color inside WebGL,
  // which can't read --playbook-epilogue-bg — so recolor it directly to the
  // live epilogue backdrop (a no-op outside the epilogue, where the
  // effective color IS the page background).
  const effectiveGlobeSettings = useMemo(() => {
    const settings = activeGlobeVariant?.settings
    if (!settings || effectiveEpilogueBackgroundColor === pageBackgroundColor) {
      return settings
    }
    return {
      ...settings,
      sphereSettings: {
        ...settings.sphereSettings,
        color: effectiveEpilogueBackgroundColor,
      },
    }
  }, [activeGlobeVariant?.settings, effectiveEpilogueBackgroundColor, pageBackgroundColor])

  return (
    <BackgroundPatternSettingsProvider initialSettings={initialBackgroundPatternSettings}>
    <LossOfControlEditorProvider>
    <PerspectiveTabContext.Provider value={perspectiveTabContextValue}>
    <MetricDetailsProvider modelSeries={explainerModelSeries} comparisonPlan={metricComparisonPlan}>
    <ChoosePathDesignContext.Provider value={CHOOSE_PATH_DIAGRAM_DESIGNS[hudVersionIndex] ?? "stock"}>
    <div
      style={{
        backgroundColor: pageBackgroundColor,
        color: pageTextColor,
        ['--page-background' as string]: pageBackgroundColor,
        ['--playbook-epilogue-bg' as string]: effectiveEpilogueBackgroundColor,
        // Matches the overlay fade duration so elements painting
        // --playbook-epilogue-bg transition in step.
        ['--playbook-epilogue-bg-fade-ms' as string]: `${galaxyFadeInDurationMs}ms`,
      }}
    >
      <style>{`body { background-color: ${pageBackgroundColor}; color: ${pageTextColor}; }`}</style>
      {IS_PREVIEW_MODE && !modalDismissed && (
        <DoNotShareModal onDismiss={() => setModalDismissed(true)} />
      )}
      {/* Centered content band holding the text column (left) and the
          fixed HUD's reserved track (right). Both margins are the SAME
          SIDE_GUTTER_CSS, so the two outer gutters are equal by
          construction at every width (this fixes the old uneven-gutter
          bug) AND those equal margins are the SOLE driver of the band's
          width: it fills 100vw - 2*SIDE_GUTTER, which resolves to the
          cluster width on wide screens and shrinks (min gutters pinned)
          on narrow ones. We deliberately do NOT also set a maxWidth —
          a px-based cap rounds differently from the vw-based margins and
          leaves the content box a few px short, which would make the
          flexible 700px text track give up width it shouldn't. The
          --plana-* vars feed the grid's explicit column widths and gap.
          NOTE: do not switch this to `transform` — a transformed
          ancestor would become the containing block for the fixed HUD
          and break it. */}
      <div
        className="px-1.5 sm:px-6 relative"
        style={{
          color: pageTextColor,
          marginLeft: BAND_MARGIN_LEFT_CSS,
          marginRight: BAND_MARGIN_RIGHT_CSS,
          // Capped so the text column shrinks (rather than sliding under
          // the fixed HUD) once the viewport is too narrow to hold the
          // full 700px plus the >=50px center gutter and the HUD.
          ['--plana-text-col' as string]: TEXT_COL_WIDTH_CSS,
          // No --plana-hud-col: the grid's right track is `1fr` (pure
          // spacer for the fixed HUD), so it needs no explicit width.
          ['--plana-center-gutter' as string]: CENTER_GUTTER_CSS,
        }}
      >
      <GoodEndingHeader />
      {/* Mobile exploration-tab switcher: ?hud=N is unusable on a phone
          (no keyboard shortcuts, URL editing is painful), so multi-variant
          exploration builds get tappable chips instead. Production builds
          carry a single variant and never render this. */}
      {isDesktopHudViewport === false && HUD_VARIANTS.length > 1 && (
        <div
          className="fixed right-2.5 z-[45] flex gap-1.5"
          style={{ top: "calc(10px + env(safe-area-inset-top))" }}
        >
          {HUD_VARIANTS.map((_, variantIndex) => (
            <button
              key={variantIndex}
              type="button"
              aria-label={`Switch to HUD variant ${variantIndex}`}
              className="flex h-8 w-8 items-center justify-center rounded-full font-mono text-[13px] font-bold leading-none"
              style={{
                border: `1.5px solid ${pageTextColor}`,
                ...(variantIndex === hudVersionIndex
                  ? { backgroundColor: pageTextColor, color: pageBackgroundColor }
                  : { backgroundColor: pageBackgroundColor, color: pageTextColor }),
              }}
              onClick={() => setHudVersionIndex(variantIndex)}
            >
              {variantIndex}
            </button>
          ))}
        </div>
      )}
      {isMobileHudEnabled && isDesktopHudViewport === false && (
        <DetailsLadderHighlightContext.Provider value={activeDetailsLadderHighlight}>
        <MobileHUD
          design={MOBILE_HUD_DESIGNS[hudVersionIndex] ?? "bubble"}
          isDateHoverAutoplay={hoverAutoplayDate !== null}
          // The metric-details card mounts INSIDE MobileHUD (its live power/
          // capability blocks need MobileHUD's HudContextProvider — outside
          // it they fall back to the timeline-start year).
          metricPanelDesign={activeMetricPanelDesign}
          currentDate={mobileHudDate}
          planAData={hudPlaybookData}
          currentBranchNodeTitle={currentBranchNodeTitle}
          selectedPlan={hudSelectedPlan}
          activePlan={hudActivePlan}
          hoveredPlan={hoveredPlan}
          isChoicePointOnScreen={isChoicePointOnScreen}
          pageTextColor={pageTextColor}
          pageBackgroundColor={pageBackgroundColor}
          globeSettings={effectiveGlobeSettings}
          countryWakeup={countryWakeup}
          stateWakeup={stateWakeup}
          legendMarkerPositions={legendMarkerPositions}
          markdownLineCounts={markdownLineCounts}
          selectedPath={selectedPath}
          hoveredBranchTargetId={hoveredBranchTargetId}
          isEpilogueExpanded={isEpilogueExpanded}
          isEpilogueScrolledTo={isEpilogueScrolledTo}
          showGalaxy={showGalaxy}
          isHoverPreviewActive={isHoverPreviewActive}
          isAgentPopulationHidden={isAgentPopulationHidden}
          isPolicyGraphHidden={isPolicyGraphHidden}
          onNodeSelect={handleHudNodeSelect}
        />
        </DetailsLadderHighlightContext.Provider>
      )}
      <div className={`${MAIN_CONTENT_GRID_CLASSES} mt-4`}>
        <div ref={contentSectionRef} className="w-full max-w-[700px] min-w-0 z-[11] relative good-ending-text">
          {perspectiveDocMarkdown ? (
            <PerspectiveFlatDocument
              markdown={perspectiveDocMarkdown}
              selectedPerspective={selectedPerspective}
            />
          ) : (
            <PlaybookBranchNode
              nodeId={branchData.rootId}
              rootId={branchData.rootId}
              nodes={branchData.nodes}
              selectedPath={selectedPath}
              setSelectedPath={setSelectedPathFromUser}
              onPlanHover={setHoveredPlan}
              onBranchHover={setHoveredBranchTargetId}
              activePlan={activePlan}
              isEpilogueExpanded={isEpilogueExpanded}
              activePov={activePov}
              perspectiveBodies={perspectiveBodies}
              selectedPerspective={selectedPerspective ?? "mainScenario"}
              showPerspectiveAboveFlowchart={showPerspectiveAboveFlowchart}
              onShowPerspectiveAboveFlowchart={() => setShowPerspectiveAboveFlowchart(true)}
              onSelectPerspectiveFromLeaf={setSelectedPerspective}
            />
          )}
        </div>
        {isDesktopHudViewport === true && (
          <div className="hidden md:block min-w-0 mx-auto">
            {/* `fixed` so the HUD stays pinned at top-6 (right under the menu) as
                the page scrolls; the slide-up rests it below the nav at the very
                top and settles it up as the nav scrolls away. Right edge matches
                the nav via the shared HUD_RIGHT_EDGE_CSS derivation. */}
            <div
              className="fixed top-6 flex-col flex items-start w-[455px] h-[calc(100vh-3rem)] z-[11]"
              style={{ right: HUD_RIGHT_EDGE_CSS }}
            >
              <DetailsLadderHighlightContext.Provider value={activeDetailsLadderHighlight}>
              <HudShell
                currentDate={hudDate}
                planAData={hudPlaybookData}
                currentBranchNodeTitle={currentBranchNodeTitle}
                selectedPlan={hudSelectedPlan}
                activePlan={hudActivePlan}
                hoveredPlan={hoveredPlan}
                isChoicePointOnScreen={isChoicePointOnScreen}
                pageTextColor={pageTextColor}
                pageBackgroundColor={pageBackgroundColor}
                globeSettings={effectiveGlobeSettings}
                countryWakeup={countryWakeup}
                stateWakeup={stateWakeup}
                legendMarkerPositions={legendMarkerPositions}
                markdownLineCounts={markdownLineCounts}
                selectedPath={selectedPath}
                hoveredBranchTargetId={hoveredBranchTargetId}
                isEpilogueExpanded={isEpilogueExpanded}
                isEpilogueScrolledTo={isEpilogueScrolledTo}
                showGalaxy={showGalaxy}
                isHoverPreviewActive={isHoverPreviewActive}
                isAgentPopulationHidden={isAgentPopulationHidden}
                isPolicyGraphHidden={isPolicyGraphHidden}
                onNodeSelect={handleHudNodeSelect}
                render={activeHudVariant}
                metricPanelDesign={activeMetricPanelDesign}
              />
              </DetailsLadderHighlightContext.Provider>
              {/* The narration bar mounts inside the HUD body itself (see
                  makeBelowDetailHudBody in hud/variants.tsx) so it sits
                  directly below the labor distribution strip. */}
            </div>
          </div>
        )}
      </div>
      {/* Epilogue dim, behind the galaxy layer. Fades in the moment
          the epilogue opens (matching the Earth-centered HUD mode, which
          engages on the Epilogue click) and back out when the reader
          scrolls up past the nav grid. The galaxy layer above uses the
          same background color, so the backdrop holds this single shade
          rather than darkening as the galaxy fades in. Mounted only while
          engaged or fading: an always-mounted fixed inset-0 overlay (even
          at opacity 0) is composited and forces everything above it into
          its own repainting GPU layer. */}
      {epilogueGalaxiesEnabled && (isEpilogueDimEngaged || isEpilogueDimMounted) && (
        <div
          className="fixed inset-0 transition-opacity pointer-events-none z-[9]"
          style={{
            opacity: isEpilogueDimEngaged ? 1 : 0,
            transitionDuration: `${galaxyFadeInDurationMs}ms`,
            backgroundColor: EPILOGUE_INTERMEDIATE_BACKGROUND_COLOR,
          }}
        />
      )}
      {epilogueGalaxiesEnabled && (showGalaxy || isGalaxyMounted) && (
      <div
        className="fixed inset-0 transition-opacity pointer-events-none z-[10]"
        style={{
          opacity: showGalaxy ? 1 : 0,
          pointerEvents: showGalaxy ? 'auto' : 'none',
          transitionDuration: `${galaxyFadeInDurationMs}ms`,
          backgroundColor: EPILOGUE_INTERMEDIATE_BACKGROUND_COLOR,
        }}
      >
        <div style={{ transform: `translateX(${galaxyTranslateX}px)`, width: '100%', height: '100%' }}>
          {isGalaxyMounted && (
            <Suspense fallback={null}>
              {/* The scene renders no Earth: the only Earth visible should be
                  the original Globe from the HUD. */}
              <GalaxyVisualizationEmbed
                progress={galaxyProgress}
                backgroundColor={EPILOGUE_INTERMEDIATE_BACKGROUND_COLOR}
                sceneStyle={GALAXY_DITHER_DOT_MASK_STYLE}
                hideEarth={true}
                hoverExclusionLeftPx={galaxyHoverExclusionLeft}
              />
            </Suspense>
          )}
        </div>
      </div>
      )}
      {activeGlobeVariant && isEditOpen && (
        <GlobeControls2
          key={activeGlobeVariant.id}
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          initialSettings={activeGlobeVariant.settings}
          onSettingsChange={(settings) =>
            setGlobeVariantsState((previousVariants) =>
              previousVariants.map((variant, index) =>
                index === activeVariantIndex ? { ...variant, settings } : variant
              )
            )
          }
          variantId={activeGlobeVariant.id}
          panelClassName="
            fixed top-[100px] left-4 z-[9999] max-h-[calc(100vh-110px)]
            overflow-auto px-3 py-2 w-[320px]
          "
        />
      )}
      {/* Dev-only ⌘+G/Ctrl+G background-pattern panel; its keydown listener
          must not mount in production (it also hijacks the browser shortcut). */}
      {process.env.NODE_ENV === "development" && (
        <BackgroundPatternControls
          panelClassName="fixed bottom-4 left-4 z-[9999]"
          isAgentPopulationHidden={isAgentPopulationHidden}
          onAgentPopulationHiddenChange={setIsAgentPopulationHidden}
          isPolicyGraphHidden={isPolicyGraphHidden}
          onPolicyGraphHiddenChange={setIsPolicyGraphHidden}
        />
      )}
      </div>
    </div>
    {/* Hidden, mounted-after-first-paint pre-render of every plan's content so
        selecting/hovering a plan is instant. Only for the branching playbook —
        the perspective flat doc has no plans to warm. Rendered last so its
        duplicate ids/markers lose to the real ones in document order (and its
        markers are excluded from every global scan). */}
    {!perspectiveDocMarkdown && <PlanPrerenderWarmup nodes={branchData.nodes} />}
    <ChoicePointScrollLock selectedPlan={selectedPlan} contentSectionRef={contentSectionRef} />
    </ChoosePathDesignContext.Provider>
    </MetricDetailsProvider>
    </PerspectiveTabContext.Provider>
    </LossOfControlEditorProvider>
    </BackgroundPatternSettingsProvider>
  )
}
